import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useDropzone } from 'react-dropzone';
import { PiWarningCircleLight, PiArrowsVerticalLight, PiBriefcaseLight, PiCalendarCheckLight, PiCheckCircleLight, PiEyeLight, PiFileTextLight, PiGlobeLight, PiCircleNotchLight, PiLockKeyLight, PiMapPinLight, PiPlusLight, PiMagnifyingGlassLight, PiTrashLight, PiUploadSimpleLight, PiUsersLight, PiLightningLight } from 'react-icons/pi';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import useJobStore from '../../store/jobStore';
import useJobProcessingStore from '../../store/jobProcessingStore';
import { formatCompensationRange } from '../../utils/compensation';

const formatNumber = (value) => new Intl.NumberFormat('en-US').format(Number(value) || 0);

const formatDate = (value) => {
  if (!value) return 'Not published';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not published';
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
};

const labelize = (value) => (
  String(value || 'Not set')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase())
);

const formatMoney = (job) => formatCompensationRange(job.payRange, 'Compensation not set');

const statusStyles = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  draft: 'bg-slate-50 text-slate-600 border-slate-200',
  paused: 'bg-amber-50 text-amber-700 border-amber-100',
  filled: 'bg-sky-50 text-sky-700 border-sky-100',
  expired: 'bg-rose-50 text-rose-700 border-rose-100'
};

const inputClass = 'h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100';

const isProcessingJob = (job) => ['pending', 'processing'].includes(job.processing?.status);
const NON_BLOCKING_PROCESSING_STAGES = new Set([
  'core_sections_ready',
  'applying_sections',
  'building_matching_profile',
  'matching_profile_queued'
]);
const PUBLISH_READY_PROCESSING_STAGES = new Set(['core_sections_ready', 'applying_sections']);
const isProcessingJobBlocking = (job) => (
  isProcessingJob(job) && !NON_BLOCKING_PROCESSING_STAGES.has(job.processing?.stage)
);

const ProcessingBadge = ({ job }) => {
  if (!job.processing) return null;
  const active = isProcessingJob(job);
  const background = active && !isProcessingJobBlocking(job);
  const publishReady = active && PUBLISH_READY_PROCESSING_STAGES.has(job.processing?.stage);
  const failed = job.processing.status === 'failed';
  const completed = job.processing.status === 'completed';
  if (completed && !job.processing.needsReview) return null;

  return (
    <span className={`mt-1 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${
      publishReady ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' : active ? 'bg-sky-50 text-sky-700 ring-sky-200' : failed ? 'bg-rose-50 text-rose-700 ring-rose-200' : 'bg-emerald-50 text-emerald-700 ring-emerald-200'
    }`}>
      {active && (publishReady
        ? <PiCheckCircleLight size={12} />
        : <PiCircleNotchLight className="animate-spin" size={12} />)}
      {active
        ? publishReady
          ? '100% - Ready to publish'
          : background
          ? `${Number(job.processing.progress || 0)}% enrichment running`
          : `${Number(job.processing.progress || 0)}% AI drafting`
        : failed ? 'AI needs attention' : 'Ready for review'}
    </span>
  );
};

const EmptyState = ({ onCreate, isHighlighting }) => (
  <div className="flex min-h-[320px] flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 bg-white px-6 text-center">
    <div className="mb-4 flex items-center justify-center text-sky-600">
      <PiBriefcaseLight size={26} />
    </div>
    <h2 className="font-display text-xl font-normal text-[#0b1f3a]">No jobs found</h2>
    <p className="mt-2 max-w-md text-sm leading-6 text-slate-500">
      Create a job post to start discovering and matching with top candidates instantly.
    </p>
    <button
      onClick={onCreate}
      className={`mt-5 inline-flex h-10 items-center gap-2 rounded-lg px-4 text-sm font-medium text-white btn-premium-base transition-colors duration-1000 ${
        isHighlighting
          ? 'bg-black btn-premium-highlight'
          : 'bg-[#0b1f3a] hover:bg-[#12345c]'
      }`}
    >
      <PiPlusLight size={13} /> Create job
    </button>
  </div>
);

const Metric = ({ icon: Icon, label, value, detail, tone = 'sky' }) => {
  const tones = {
    sky: 'text-sky-600',
    slate: 'text-slate-800',
    green: 'text-emerald-600',
    amber: 'text-amber-600'
  };

  return (
    <div className="min-w-0 rounded-lg border border-slate-200/80 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.035)]">
      <div className="mb-4 flex items-center justify-between gap-3">
        <span className={`flex shrink-0 items-center justify-center ${tones[tone] || tones.sky}`}>
          <Icon size={24} />
        </span>
        <span className="truncate text-xs font-medium text-slate-400">{detail}</span>
      </div>
      <div className="truncate font-display text-2xl font-normal leading-none text-[#0b1f3a]">{value}</div>
      <div className="mt-2 truncate text-sm font-medium text-slate-700">{label}</div>
    </div>
  );
};

const LocalSkeletonBlock = ({ className = '', style }) => (
  <div
    className={`relative overflow-hidden bg-slate-200/60 ${className}`}
    style={style}
  >
    <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent" />
  </div>
);

const JobsOverviewSkeleton = () => (
  <div className="min-h-screen bg-white p-4 sm:p-6 lg:p-8">
    <style>{`
      @keyframes shimmer {
        100% { transform: translateX(100%); }
      }
      .animate-shimmer {
        animation: shimmer 1.5s infinite;
      }
    `}</style>
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="space-y-3">
          <LocalSkeletonBlock className="h-3 w-16 rounded-md" />
          <LocalSkeletonBlock className="h-9 w-56 rounded-md" />
          <LocalSkeletonBlock className="h-4 w-[32rem] max-w-full rounded-md" />
        </div>
        <LocalSkeletonBlock className="h-10 w-32 rounded-lg" />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={index} className="rounded-lg border border-slate-200/80 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.035)]">
            <div className="mb-4 flex items-center justify-between gap-3">
              <LocalSkeletonBlock className="h-9 w-9 rounded-lg" />
              <LocalSkeletonBlock className="h-3 w-20 rounded-md" />
            </div>
            <LocalSkeletonBlock className="h-8 w-20 rounded-md" />
            <LocalSkeletonBlock className="mt-2 h-4 w-24 rounded-md" />
          </div>
        ))}
      </div>

      <section className="rounded-lg border border-slate-200/80 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.035)]">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-2">
            <LocalSkeletonBlock className="h-5 w-40 rounded-md" />
            <LocalSkeletonBlock className="h-3 w-80 max-w-full rounded-md" />
          </div>
          <div className="flex flex-wrap gap-2">
            {Array.from({ length: 4 }).map((_, index) => <LocalSkeletonBlock key={index} className="h-7 w-24 rounded-md" />)}
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200/80 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
        <div className="flex flex-col gap-3 border-b border-slate-100 p-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-1 flex-col gap-3 sm:flex-row">
            <LocalSkeletonBlock className="h-10 flex-1 rounded-lg sm:max-w-sm" />
            <LocalSkeletonBlock className="h-10 w-full rounded-lg sm:w-40" />
            <LocalSkeletonBlock className="h-10 w-full rounded-lg sm:w-44" />
          </div>
          <div className="flex items-center gap-2">
            <LocalSkeletonBlock className="h-10 w-32 rounded-lg" />
            <LocalSkeletonBlock className="h-10 w-40 rounded-lg" />
          </div>
        </div>

        <div className="px-4 py-2">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="grid min-w-[980px] grid-cols-[2.2fr_1fr_1.3fr_1fr_1fr_1fr_1.2fr] gap-4 border-b border-slate-100 py-4 last:border-0">
              <div className="space-y-2">
                <LocalSkeletonBlock className="h-4 w-40 rounded-md" />
                <LocalSkeletonBlock className="h-3 w-52 rounded-md" />
              </div>
              <LocalSkeletonBlock className="h-7 w-20 rounded-md" />
              <LocalSkeletonBlock className="h-4 w-28 rounded-md" />
              <LocalSkeletonBlock className="h-4 w-16 rounded-md" />
              <LocalSkeletonBlock className="h-4 w-14 rounded-md" />
              <LocalSkeletonBlock className="h-4 w-20 rounded-md" />
              <div className="flex justify-end gap-1.5">
                {Array.from({ length: 4 }).map((__, buttonIndex) => <LocalSkeletonBlock key={buttonIndex} className="h-8 w-8 rounded-md" />)}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  </div>
);

const JobRow = ({ job, onOpen, onMatches, onPublishToggle, onDelete, busy }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const active = job.status === 'active';
  const processing = isProcessingJobBlocking(job);

  return (
    <>
      <tr className={`group transition-colors hover:bg-slate-50/60 last:border-0 ${isExpanded ? '' : 'border-b border-slate-100'}`}>
        <td className="py-4 pl-4 pr-4 align-top">
          <div className="flex items-start gap-3">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="mt-0.5 shrink-0 rounded text-slate-400 hover:bg-slate-200 hover:text-slate-700 transition-all"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}>
                <path d="m9 18 6-6-6-6"/>
              </svg>
            </button>
            <div className="min-w-0 pt-0.5">
              <button onClick={() => onOpen(job)} className="block max-w-[320px] text-left">
                <span className="block truncate text-[14px] font-medium text-[#0b1f3a] hover:text-sky-700">{job.title || 'Untitled role'}</span>
              </button>
              <ProcessingBadge job={job} />
            </div>
          </div>
        </td>
        <td className="py-4 pr-4 align-top">
          <span className={`mt-0.5 inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-medium capitalize ${statusStyles[job.status] || statusStyles.draft}`}>
            {labelize(job.status)}
          </span>
        </td>
        <td className="py-4 pr-4 align-top">
          <div className="mt-0.5 whitespace-nowrap text-sm font-medium text-slate-600">
            {formatMoney(job)}
          </div>
        </td>
        <td className="py-4 pr-4 align-top">
          <div className="mt-0.5 text-sm font-medium text-[#0b1f3a]">
            {formatNumber(job.positionsFilled)} <span className="font-normal text-slate-400">/ {formatNumber(job.positionsAvailable)}</span>
          </div>
        </td>
        <td className="py-4 pr-4 align-top">
          {processing ? (
            <span className="mt-0.5 inline-flex items-center gap-1.5 rounded-full bg-sky-50 px-2 py-1 text-[11px] font-medium text-sky-700 ring-1 ring-inset ring-sky-200">
              <PiCircleNotchLight className="animate-spin" size={12} /> Matching profile pending
            </span>
          ) : job.stats?.totalMatches > 0 ? (
            <div className="flex flex-col gap-1.5 mt-0.5">
              <span className="text-sm font-medium text-[#0b1f3a]">{formatNumber(job.stats.totalMatches)} candidates</span>
              <div className="flex items-center gap-2">
                <div className="h-1.5 w-16 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-emerald-400"
                    style={{ width: `${Math.min(100, job.stats.averageMatchScore || 0)}%` }}
                  />
                </div>
                <span className="text-[11px] font-medium text-slate-500">{job.stats.averageMatchScore || 0}% avg</span>
              </div>
            </div>
          ) : (
            <span className="mt-0.5 inline-flex items-center rounded-full bg-slate-50 px-2 py-1 text-[11px] font-medium text-slate-500 ring-1 ring-inset ring-slate-200">
              Evaluating...
            </span>
          )}
        </td>
        <td className="py-4 pr-4 align-top">
          <div className="mt-0.5 flex flex-col gap-0.5">
            <span className="text-sm font-medium text-[#0b1f3a] whitespace-nowrap">{formatDate(job.publishedAt || job.createdAt)}</span>
            <span className="text-[11px] text-slate-400">{job.publishedAt ? 'Published' : 'Created'}</span>
          </div>
        </td>
        <td className="py-4 pr-4 align-top">
          <div className="flex justify-end gap-1.5">
            <button
              onClick={() => onOpen(job)}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700"
              title="Open job"
            >
              <PiEyeLight size={12} />
            </button>
            <button
              onClick={() => onMatches(job)}
              disabled={processing}
              className="inline-flex h-8 items-center gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 text-xs font-medium text-sky-700 transition hover:border-sky-300 hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <PiUsersLight size={12} /> Matches
            </button>
            <button
              onClick={() => onPublishToggle(job)}
              disabled={busy || processing}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700 disabled:opacity-50"
              title={processing ? 'Available after AI drafting' : active ? 'Close job' : 'Publish job'}
            >
              {active ? <PiLockKeyLight size={12} /> : <PiGlobeLight size={12} />}
            </button>
            <button
              onClick={() => onDelete(job)}
              disabled={busy}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700 disabled:opacity-50"
              title="Delete job"
            >
              <PiTrashLight size={12} />
            </button>
          </div>
        </td>
      </tr>
      {isExpanded && (
        <tr className="border-b border-slate-100 bg-white/50">
          <td colSpan={7} className="pb-5 pl-12 pr-4 pt-2">
            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="grid grid-cols-4 gap-6">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Target Location</p>
                  <p className="mt-1 flex items-center gap-1.5 text-sm font-medium text-[#0b1f3a]">
                    <PiMapPinLight size={12} className="text-slate-400" />
                    {job.location || 'Remote'}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Experience Required</p>
                  <p className="mt-1 text-sm font-medium text-[#0b1f3a]">{labelize(job.experienceLevel)}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Job Type</p>
                  <p className="mt-1 text-sm font-medium text-[#0b1f3a]">{labelize(job.jobType)}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Job ID</p>
                  <p className="mt-1 text-xs font-mono text-slate-500">{job.id}</p>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const JobCard = ({ job, onOpen, onMatches, onPublishToggle, onDelete, busy }) => {
  const active = job.status === 'active';
  const processing = isProcessingJobBlocking(job);

  return (
    <article className="min-w-0 rounded-lg border border-slate-200/80 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.035)] transition hover:border-sky-200">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <button onClick={() => onOpen(job)} className="block max-w-full truncate text-left font-display text-[17px] font-normal leading-tight text-[#0b1f3a] hover:text-sky-700">
            {job.title || 'Untitled role'}
          </button>
          <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-400">
            <span className="inline-flex items-center gap-1"><PiMapPinLight size={12} /> {job.location || 'No location'}</span>
            <span>{labelize(job.jobType)}</span>
            <span>{labelize(job.experienceLevel)}</span>
          </div>
          <ProcessingBadge job={job} />
        </div>
        <span className={`shrink-0 rounded-full border px-2.5 py-0.5 text-[11px] font-medium capitalize ${statusStyles[job.status] || statusStyles.draft}`}>
          {labelize(job.status)}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 border-y border-slate-100 py-4">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">Matches</div>
          <div className="mt-1">
            {processing ? (
              <span className="inline-flex items-center gap-1 rounded-md bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-700 ring-1 ring-inset ring-sky-200">
                <PiCircleNotchLight className="animate-spin" size={12} /> Profile pending
              </span>
            ) : job.stats?.totalMatches > 0 ? (
              <span className="text-sm font-medium text-[#0b1f3a]">{formatNumber(job.stats.totalMatches)}</span>
            ) : (
              <span className="inline-flex items-center rounded-md bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-inset ring-amber-600/20">
                Finding candidates...
              </span>
            )}
          </div>
        </div>
        <div>
          <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">Positions</div>
          <div className="mt-1 text-sm font-medium text-[#0b1f3a]">{formatNumber(job.positionsFilled)} / {formatNumber(job.positionsAvailable)}</div>
        </div>
        <div className="col-span-2">
          <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">Compensation</div>
          <div className="mt-1 truncate text-sm font-medium text-[#0b1f3a]">{formatMoney(job)}</div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs text-slate-500">{formatDate(job.publishedAt || job.createdAt)}</div>
        <div className="flex gap-1.5">
          <button onClick={() => onMatches(job)} disabled={processing} className="inline-flex h-9 items-center gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 text-xs font-medium text-sky-700 transition hover:border-sky-300 hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-50">
            <PiUsersLight size={12} /> Matches
          </button>
          <button onClick={() => onPublishToggle(job)} disabled={busy || processing} className="flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700 disabled:opacity-50" title={processing ? 'Available after AI drafting' : active ? 'Close job' : 'Publish job'}>
            {active ? <PiLockKeyLight size={12} /> : <PiGlobeLight size={12} />}
          </button>
          <button onClick={() => onDelete(job)} disabled={busy} className="flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700 disabled:opacity-50">
            <PiTrashLight size={12} />
          </button>
        </div>
      </div>
    </article>
  );
};

const JobsOverview = () => {
  const navigate = useNavigate();
  const startImport = useJobProcessingStore(state => state.startImport);
  const {
    jobs,
    jobsLoading,
    error,
    jobFilters,
    fetchEmployerJobs,
    fetchMatchingConfiguration,
    setJobFilters,
    publishJob,
    unpublishJob,
    deleteJob,
    clearError
  } = useJobStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [viewFormat, setViewFormat] = useState('table');
  const [busyJobId, setBusyJobId] = useState(null);
  const [isHighlighting, setIsHighlighting] = useState(true);
  const [importing, setImporting] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [jobPendingDeletion, setJobPendingDeletion] = useState(null);
  const backendSearch = searchTerm.trim();

  useEffect(() => {
    const timer = setTimeout(() => setIsHighlighting(false), 3500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    fetchMatchingConfiguration();
  }, [fetchMatchingConfiguration]);

  useEffect(() => {
    fetchEmployerJobs({
      status: jobFilters.status,
      search: backendSearch.length >= 2 ? backendSearch : undefined,
      sortBy: jobFilters.sortBy,
      sortOrder: jobFilters.sortOrder,
      limit: 100,
      offset: 0
    });
  }, [backendSearch, fetchEmployerJobs, jobFilters.status, jobFilters.sortBy, jobFilters.sortOrder]);

  const filteredJobs = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return jobs;
    return jobs.filter(job => (
      job.title?.toLowerCase().includes(query) ||
      job.location?.toLowerCase().includes(query) ||
      job.jobType?.toLowerCase().includes(query)
    ));
  }, [jobs, searchTerm]);

  const metrics = useMemo(() => {
    const totalMatches = jobs.reduce((sum, job) => sum + Number(job.stats?.totalMatches || 0), 0);
    const mutualMatches = jobs.reduce((sum, job) => sum + Number(job.stats?.mutualMatches || 0), 0);
    const openPositions = jobs.reduce((sum, job) => sum + Number(job.positionsRemaining || 0), 0);

    return {
      totalJobs: jobs.length,
      activeJobs: jobs.filter(job => job.status === 'active').length,
      totalMatches,
      mutualMatches,
      openPositions
    };
  }, [jobs]);

  const reload = () => fetchEmployerJobs({
    status: jobFilters.status,
    search: backendSearch.length >= 2 ? backendSearch : undefined,
    sortBy: jobFilters.sortBy,
    sortOrder: jobFilters.sortOrder,
    limit: 100,
    offset: 0
  });

  const withBusyJob = async (job, action) => {
    setBusyJobId(job.id);
    try {
      await action();
    } finally {
      setBusyJobId(null);
    }
  };

  const handlePublishToggle = (job) => withBusyJob(job, async () => {
    const result = job.status === 'active' ? await unpublishJob(job.id) : await publishJob(job.id);
    if (result.success) {
      toast.success(job.status === 'active'
        ? 'Job paused.'
        : result.matchingResults?.skipped ? 'Job published. Immediate matching is disabled.' : 'Job published. Matching will finish in the background.');
      reload();
    } else {
      toast.error(result.error);
    }
  });

  const handleDelete = (job) => setJobPendingDeletion(job);

  const confirmDelete = () => {
    const job = jobPendingDeletion;
    if (!job) return;

    withBusyJob(job, async () => {
      const result = await deleteJob(job.id);
      if (result.success) {
        setJobPendingDeletion(null);
        toast.success('Job and its candidate matches were deleted.');
      } else {
        toast.error(result.error);
      }
    });
  };

  const handleDropAndGo = async (input) => {
    const file = typeof File !== 'undefined' && input instanceof File ? input : input.target?.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const result = await startImport(file, { notifyEmail });
      if (result.success) {
        toast.success('Draft created. AI is working in the background.');
      } else {
        toast.error(result.error);
      }
    } finally {
      setImporting(false);
    }
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: openImportPicker
  } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt']
    },
    maxSize: 10 * 1024 * 1024,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    noDragEventsBubbling: true,
    disabled: importing,
    onDropAccepted: (files) => handleDropAndGo(files[0]),
    onDropRejected: () => toast.error('Upload one valid PDF, DOC, DOCX, or TXT file smaller than 10MB.')
  });

  if (jobsLoading && jobs.length === 0) {
    return <JobsOverviewSkeleton />;
  }

  return (
    <div {...getRootProps({ 'data-job-import-dropzone': 'local' })} className="relative min-h-screen bg-white p-4 sm:p-6 lg:p-8">
      <input {...getInputProps()} />
      {isDragActive && (
        <div className="pointer-events-none fixed inset-4 z-50 flex items-center justify-center rounded-2xl border-2 border-dashed border-sky-400 bg-[#0b1f3a]/95 text-white shadow-2xl">
          <div className="text-center">
            <PiUploadSimpleLight className="mx-auto text-sky-300" size={29} />
            <div className="mt-4 font-display text-2xl">Release to create a durable draft</div>
            <div className="mt-2 text-sm text-slate-300">You can keep working as soon as the upload finishes.</div>
          </div>
        </div>
      )}
      <style>{`
        @keyframes shimmer-sweep {
          0% { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
        @keyframes subtle-pulse-ring {
          0% { box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.25); }
          50% { box-shadow: 0 0 0 3px rgba(0, 0, 0, 0); }
          100% { box-shadow: 0 0 0 0 rgba(0, 0, 0, 0); }
        }
        .btn-premium-base {
          position: relative;
          overflow: hidden;
        }
        .btn-premium-base::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(110deg, transparent 25%, rgba(255, 255, 255, 0.4) 50%, transparent 75%);
          background-size: 200% 100%;
          animation: shimmer-sweep 2s infinite linear;
          opacity: 0;
          transition: opacity 1s ease-in-out;
          pointer-events: none;
        }
        .btn-premium-highlight::after {
          opacity: 1;
        }
        .btn-premium-highlight {
          animation: subtle-pulse-ring 2s infinite cubic-bezier(0.4, 0, 0.2, 1);
        }
      `}</style>
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.16em] text-sky-700">Jobs</p>
            <h1 className="mt-2 font-display text-3xl font-normal tracking-tight text-[#0b1f3a]">Active Roles</h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
              Manage your open positions, track candidate matches, and oversee your hiring workflow.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-600">
              <input
                type="checkbox"
                checked={notifyEmail}
                onChange={(event) => setNotifyEmail(event.target.checked)}
                className="rounded border-slate-300 text-sky-600 focus:ring-sky-500"
              />
              Email when ready
            </label>
            <button
              onClick={openImportPicker}
              disabled={importing}
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-sky-200 bg-white px-4 text-sm font-medium text-sky-700 transition hover:bg-sky-50 disabled:opacity-60"
            >
              {importing ? <PiCircleNotchLight className="animate-spin" size={13} /> : <PiUploadSimpleLight size={13} />} Drop a JD
            </button>
            <button
              onClick={() => navigate('/jobs/create')}
              className={`inline-flex h-10 items-center gap-2 rounded-lg px-4 text-sm font-medium text-white btn-premium-base transition-colors duration-1000 ${
                isHighlighting
                  ? 'bg-black btn-premium-highlight'
                  : 'bg-[#0b1f3a] hover:bg-[#12345c]'
              }`}
            >
              <PiPlusLight size={13} /> Create job
            </button>
          </div>
        </div>

        {error && (
          <div className="flex items-start justify-between gap-4 rounded-lg border border-rose-100 bg-rose-50 p-4 text-sm text-rose-700">
            <div className="flex gap-2">
              <PiWarningCircleLight size={14} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
            <button onClick={clearError} className="text-xs font-medium text-rose-700 hover:text-rose-900">Dismiss</button>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <Metric icon={PiBriefcaseLight} label="Total jobs" value={formatNumber(metrics.totalJobs)} detail="Total pipeline" tone="slate" />
          <Metric icon={PiLightningLight} label="Active jobs" value={formatNumber(metrics.activeJobs)} detail="Published" tone="sky" />
          <Metric icon={PiUsersLight} label="Total matches" value={formatNumber(metrics.totalMatches)} detail="Matches" tone="green" />
          <Metric icon={PiCheckCircleLight} label="Mutual interest" value={formatNumber(metrics.mutualMatches)} detail="Connected" tone="amber" />
          <Metric icon={PiCalendarCheckLight} label="Open positions" value={formatNumber(metrics.openPositions)} detail="Remaining seats" tone="slate" />
        </div>

        <section className="rounded-lg border border-slate-200/80 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
          <div className="flex flex-col gap-3 border-b border-slate-100 p-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-1 flex-col gap-3 sm:flex-row">
              <div className="relative min-w-0 flex-1 sm:max-w-sm">
                <PiMagnifyingGlassLight className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={13} />
                <input
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search jobs"
                  className={`${inputClass} w-full pl-9`}
                />
              </div>
              <select
                value={jobFilters.status}
                onChange={(event) => setJobFilters({ status: event.target.value })}
                className={inputClass}
              >
                <option value="all">All statuses</option>
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="filled">Filled</option>
                <option value="expired">Expired</option>
              </select>
              <select
                value={`${jobFilters.sortBy}:${jobFilters.sortOrder}`}
                onChange={(event) => {
                  const [sortBy, sortOrder] = event.target.value.split(':');
                  setJobFilters({ sortBy, sortOrder });
                }}
                className={inputClass}
              >
                <option value="created_at:DESC">Newest first</option>
                <option value="updated_at:DESC">Recently updated</option>
                <option value="title:ASC">Title A-Z</option>
                <option value="match_count:DESC">Most matches</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              {jobsLoading && jobs.length > 0 && (
                <span className="inline-flex h-9 items-center gap-2 rounded-lg border border-sky-100 bg-[#eef9ff] px-3 text-xs font-medium text-sky-700">
                  <PiCircleNotchLight className="animate-spin" size={12} /> Updating
                </span>
              )}
              <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
                <button
                  onClick={() => setViewFormat('table')}
                  className={`inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-xs font-medium ${viewFormat === 'table' ? 'bg-white text-[#0b1f3a] shadow-sm' : 'text-slate-500'}`}
                >
                  <PiFileTextLight size={12} /> Table
                </button>
                <button
                  onClick={() => setViewFormat('grid')}
                  className={`inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-xs font-medium ${viewFormat === 'grid' ? 'bg-white text-[#0b1f3a] shadow-sm' : 'text-slate-500'}`}
                >
                  <PiArrowsVerticalLight size={12} /> Cards
                </button>
              </div>
            </div>
          </div>

          {filteredJobs.length === 0 ? (
            <div className="p-4">
              <EmptyState onCreate={() => navigate('/jobs/create')} isHighlighting={isHighlighting} />
            </div>
          ) : viewFormat === 'table' ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[980px] text-left">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/50 text-[11px] uppercase tracking-[0.08em] text-slate-400">
                    <th className="py-3 pl-4 pr-4 font-medium min-w-[220px]">Role</th>
                    <th className="py-3 pr-4 font-medium min-w-[100px]">Status</th>
                    <th className="py-3 pr-4 font-medium min-w-[180px]">Compensation</th>
                    <th className="py-3 pr-4 font-medium min-w-[100px]">Positions</th>
                    <th className="py-3 pr-4 font-medium min-w-[180px]">Matches</th>
                    <th className="py-3 pr-4 font-medium min-w-[140px]">Date</th>
                    <th className="py-3 pl-4 pr-4 text-right font-medium min-w-[180px]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredJobs.map(job => (
                    <JobRow
                      key={job.id}
                      job={job}
                      busy={String(busyJobId) === String(job.id)}
                      onOpen={(item) => navigate(`/jobs/${item.id}`)}
                      onMatches={(item) => navigate(`/jobs/${item.id}/matches`)}
                      onPublishToggle={handlePublishToggle}
                      onDelete={handleDelete}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-2 xl:grid-cols-3">
              {filteredJobs.map(job => (
                <JobCard
                  key={job.id}
                  job={job}
                  busy={String(busyJobId) === String(job.id)}
                  onOpen={(item) => navigate(`/jobs/${item.id}`)}
                  onMatches={(item) => navigate(`/jobs/${item.id}/matches`)}
                  onPublishToggle={handlePublishToggle}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      <ConfirmDialog
        open={Boolean(jobPendingDeletion)}
        title={`Delete "${jobPendingDeletion?.title || 'this job'}"?`}
        description="This action removes the job from your pipeline and cannot be undone."
        confirmLabel="Delete job"
        busy={Boolean(jobPendingDeletion) && String(busyJobId) === String(jobPendingDeletion?.id)}
        onClose={() => setJobPendingDeletion(null)}
        onConfirm={confirmDelete}
      >
        <p>
          All matches and job-specific hiring activity for this role will be deleted.
          Candidate profiles and CVs will remain available for other jobs.
        </p>
      </ConfirmDialog>
    </div>
  );
};

export default JobsOverview;
